<script setup lang="ts">
import { computed, ref } from 'vue'
import AppColorPicker from '@/components/AppColorPicker.vue'
import EventPropRow from '@/components/eventEditors/EventPropRow.vue'
import ImageOptionPickerField from '@/components/ImageOptionPickerField.vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import VariableIndexField from '@/components/eventEditors/VariableIndexField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import CustomUIItem from '@/model/CustomUI/CustomUIItem'
import { useSceneEditorStore } from '@/store/sceneEditor'
import { useWorkspaceStore } from '@/store/workspace'
import { hexToRgbString, normalizeRgbString, rgbStringToHex } from '@/utils/textColor'

const editor = useSceneEditorStore()
const workspace = useWorkspaceStore()
const uiRevision = ref(0)
const propRevision = ref(0)
const selection = computed(() => editor.selectedCustomUi)
const data = computed(() => editor.selectedCustomUiData)
const item = computed(() => editor.selectedCustomUiItem)
const currentItem = computed(() => {
  uiRevision.value
  editor.customUiRevision
  return item.value
})
const isUserIndex = computed(() => {
  uiRevision.value
  editor.customUiRevision
  return Boolean(item.value?.IsUserIndex)
})
const isUserVar = computed(() => {
  uiRevision.value
  editor.customUiRevision
  return Boolean(item.value?.IsUserVar)
})
const isUserString = computed(() => {
  uiRevision.value
  editor.customUiRevision
  return Boolean(item.value?.IsUserString)
})
const xValue = computed(() => {
  uiRevision.value
  editor.customUiRevision
  return Number(item.value?.X ?? 0)
})
const yValue = computed(() => {
  uiRevision.value
  editor.customUiRevision
  return Number(item.value?.Y ?? 0)
})
const colorText = computed(() => {
  uiRevision.value
  editor.customUiRevision
  return String(item.value?.Color || '255,255,255')
})
const colorHex = computed(() => rgbStringToHex(colorText.value, '#ffffff'))
const buttonOptions = computed(() => {
  const buttons = workspace.project?.systemData?.btnList?.buttons ?? []
  return buttons.map((button, index) => ({
    index,
    label: `${index} ${button?.Name?.trim() || '按钮'}`,
    name: button?.Name?.trim() || '按钮',
    noneImagePath: button?.NoneImage?.name ?? '',
    moveImagePath: button?.MoveImage?.name ?? '',
  }))
})

function notify() {
  uiRevision.value++
  editor.notifyCustomUiChanged()
}

function refreshResourceFields(): void {
  uiRevision.value++
  propRevision.value++
  editor.notifyCustomUiChanged()
}

function updateName(value: string) {
  if (!data.value || selection.value?.itemIndex !== null) return
  if (data.value.Name === value) return
  data.value.Name = value
  notify()
}

function commitRootName(event: Event): void {
  updateName((event.target as HTMLInputElement).value)
}

function commitRootNameAndBlur(event: Event): void {
  commitRootName(event)
  ;(event.target as HTMLInputElement).blur()
}

function updateRootBoolean(key: 'IsMouseExit' | 'IsKeyExit', value: boolean): void {
  if (!data.value || selection.value?.itemIndex !== null) return
  data.value[key] = Boolean(value)
  notify()
}

function updateRootCheckbox(key: 'IsMouseExit' | 'IsKeyExit', event: Event): void {
  updateRootBoolean(key, (event.target as HTMLInputElement).checked)
}

function getDefaultItemName(type: number): string {
  if (type === CustomUIItem.controlType.Button) return '按钮'
  if (type === CustomUIItem.controlType.String) return '文本'
  if (type === CustomUIItem.controlType.Var) return '数值'
  if (type === CustomUIItem.controlType.Pic) return '图片'
  if (type === CustomUIItem.controlType.Drap) return '数值条'
  return '控件'
}

function getItemStoredName(current: CustomUIItem): string {
  return current.Type === CustomUIItem.controlType.Drap ? current.Color : current.ImagePath2
}

function getItemDisplayName(current: CustomUIItem): string {
  return getItemStoredName(current).trim() || getDefaultItemName(current.Type)
}

function updateItemName(value: string) {
  const currentSelection = selection.value
  const current = currentSelection?.itemIndex === null || !currentSelection
    ? null
    : editor.customUiList[currentSelection.uiIndex]?.ControlList[currentSelection.itemIndex] ?? item.value
  if (!current) return
  const target = current as CustomUIItem
  if (getItemStoredName(target) === value) return
  if (target.Type === CustomUIItem.controlType.Drap) target.Color = value
  else target.ImagePath2 = value
  notify()
}

function commitItemName(event: Event): void {
  updateItemName((event.target as HTMLInputElement).value)
}

function commitItemNameAndBlur(event: Event): void {
  commitItemName(event)
  ;(event.target as HTMLInputElement).blur()
}

function updateNumber(key: 'X' | 'Y' | 'Index' | 'StringIndex' | 'MaxVar', value: number) {
  const current = item.value
  if (!current) return
  current[key] = Math.trunc(Number(value) || 0)
  notify()
}

function updateBoolean(key: 'IsUserIndex' | 'IsUserVar' | 'IsUserString', value: boolean) {
  const current = item.value
  if (!current) return
  const checked = Boolean(value)
  current[key] = checked
  if (key === 'IsUserIndex') {
    current.Index = 0
    if (current.Type === CustomUIItem.controlType.String) {
      current.StringIndex = 0
    }
  }
  if (key === 'IsUserString') {
    current.StringIndex = 0
  }
  if (key === 'IsUserVar' && checked) {
    current.X = 0
    current.Y = 0
  }
  notify()
}

function updateItemCheckbox(key: 'IsUserIndex' | 'IsUserVar' | 'IsUserString', event: Event): void {
  updateBoolean(key, (event.target as HTMLInputElement).checked)
}

function updateTextColor(value: string) {
  const current = item.value
  if (!current) return
  current.Color = value
  notify()
}

function updateTextColorHex(value: string): void {
  updateTextColor(hexToRgbString(value, '255,255,255'))
}

function normalizeTextColor(): void {
  const current = item.value
  if (!current) return
  current.Color = normalizeRgbString(current.Color)
  notify()
}

function updateButtonIndex(value: number) {
  const current = item.value
  if (!current) return
  current.Index = Number(value) || 0
  notify()
}

function updateStringIndex(value: number) {
  const current = item.value
  if (!current) return
  current.StringIndex = value
  notify()
}

function updateImagePath1(value: string) {
  const current = item.value
  if (!current) return
  current.ImagePath1 = value
  refreshResourceFields()
}

function updateImagePath2(value: string) {
  const current = item.value
  if (!current) return
  current.ImagePath2 = value
  refreshResourceFields()
}

function updateMaxVar(value: number) {
  const current = item.value
  if (!current) return
  current.MaxVar = value
  notify()
}
</script>

<template>
  <div class="event-prop-form custom-ui-prop-form">
    <template v-if="selection && data && selection.itemIndex === null && selection.eventSource === 'none'">
      <EventPropRow label="名称">
        <input
          class="cell-input"
          type="text"
          :value="data.Name"
          @blur="commitRootName"
          @keydown.enter.prevent="commitRootNameAndBlur"
        />
      </EventPropRow>
      <EventPropRow label="右键退出">
        <input class="cell-checkbox" type="checkbox" :checked="Boolean(data.IsMouseExit)" @change="updateRootCheckbox('IsMouseExit', $event)" />
      </EventPropRow>
      <EventPropRow label="ESC退出">
        <input class="cell-checkbox" type="checkbox" :checked="Boolean(data.IsKeyExit)" @change="updateRootCheckbox('IsKeyExit', $event)" />
      </EventPropRow>
    </template>

    <template v-else-if="currentItem">
      <EventPropRow label="名称">
        <input
          class="cell-input"
          type="text"
          :value="getItemDisplayName(currentItem as CustomUIItem)"
          @blur="commitItemName"
          @keydown.enter.prevent="commitItemNameAndBlur"
        />
      </EventPropRow>

      <template v-if="currentItem.Type === CustomUIItem.controlType.Button">
        <EventPropRow label="索引用变量">
          <input class="cell-checkbox" type="checkbox" :checked="isUserIndex" @change="updateItemCheckbox('IsUserIndex', $event)" />
        </EventPropRow>
        <EventPropRow label="按钮索引">
          <VariableIndexField
            v-if="isUserIndex"
            :model-value="currentItem.Index"
            scope="var"
            title="选择按钮索引变量"
            @update:model-value="updateButtonIndex"
          />
          <ImageOptionPickerField
            v-else
            :key="`button-${propRevision}-${currentItem.Index}`"
            :model-value="currentItem.Index"
            title="选择按钮图片选项"
            :items="buttonOptions"
            graphic-base-folder-path="图片/button"
            @update:model-value="updateButtonIndex"
          />
        </EventPropRow>
      </template>

      <template v-else-if="currentItem.Type === CustomUIItem.controlType.String">
        <EventPropRow label="文本用变量">
          <input class="cell-checkbox" type="checkbox" :checked="isUserIndex" @change="updateItemCheckbox('IsUserIndex', $event)" />
        </EventPropRow>
        <EventPropRow label="文本">
          <VariableIndexField
            :model-value="currentItem.StringIndex"
            :scope="isUserIndex ? 'var' : 'string'"
            title="选择文本来源"
            @update:model-value="updateStringIndex"
          />
        </EventPropRow>
        <EventPropRow label="颜色">
          <div class="color-line">
            <AppColorPicker
              :model-value="colorHex"
              @update:model-value="updateTextColorHex"
            />
            <input
              class="cell-input wide"
              type="text"
              :value="colorText"
              placeholder="255,255,255"
              @input="updateTextColor(($event.target as HTMLInputElement).value)"
              @blur="normalizeTextColor"
            />
          </div>
        </EventPropRow>
      </template>

      <template v-else-if="currentItem.Type === CustomUIItem.controlType.Var">
        <EventPropRow label="数值">
          <VariableIndexField :model-value="currentItem.Index" scope="var" title="选择数值变量" @update:model-value="updateButtonIndex" />
        </EventPropRow>
        <EventPropRow label="颜色">
          <div class="color-line">
            <AppColorPicker
              :model-value="colorHex"
              @update:model-value="updateTextColorHex"
            />
            <input
              class="cell-input wide"
              type="text"
              :value="colorText"
              placeholder="255,255,255"
              @input="updateTextColor(($event.target as HTMLInputElement).value)"
              @blur="normalizeTextColor"
            />
          </div>
        </EventPropRow>
      </template>

      <template v-else-if="currentItem.Type === CustomUIItem.controlType.Pic">
        <EventPropRow label="字符串指定">
          <input class="cell-checkbox" type="checkbox" :checked="isUserString" @change="updateItemCheckbox('IsUserString', $event)" />
        </EventPropRow>
        <EventPropRow label="路径">
          <VariableIndexField
            v-if="isUserString"
            :model-value="currentItem.StringIndex"
            scope="string"
            title="选择图片路径字符串"
            @update:model-value="updateStringIndex"
          />
          <ResourceSelectField
            v-else
            :key="`pic-${propRevision}-${currentItem.ImagePath1}`"
            :model-value="currentItem.ImagePath1"
            category="graphic"
            base-folder-path="图片/其他"
            store-mode="relative"
            display-mode="full"
            @update:model-value="updateImagePath1"
          />
        </EventPropRow>
      </template>

      <template v-else-if="currentItem.Type === CustomUIItem.controlType.Drap">
        <EventPropRow label="底图">
          <ResourceSelectField
            :key="`drap-bg-${propRevision}-${currentItem.ImagePath1}`"
            :model-value="currentItem.ImagePath1"
            category="graphic"
            base-folder-path="图片/其他"
            store-mode="relative"
            display-mode="full"
            @update:model-value="updateImagePath1"
          />
        </EventPropRow>
        <EventPropRow label="滚动图">
          <ResourceSelectField
            :key="`drap-thumb-${propRevision}-${currentItem.ImagePath2}`"
            :model-value="currentItem.ImagePath2"
            category="graphic"
            base-folder-path="图片/其他"
            store-mode="relative"
            display-mode="full"
            @update:model-value="updateImagePath2"
          />
        </EventPropRow>
        <EventPropRow label="最大值">
          <VariableIndexField :model-value="currentItem.MaxVar" scope="var" title="选择最大值变量" @update:model-value="updateMaxVar" />
        </EventPropRow>
        <EventPropRow label="当前值">
          <VariableIndexField :model-value="currentItem.Index" scope="var" title="选择当前值变量" @update:model-value="updateButtonIndex" />
        </EventPropRow>
      </template>

      <EventPropRow label="位置用变量">
        <input class="cell-checkbox" type="checkbox" :checked="isUserVar" @change="updateItemCheckbox('IsUserVar', $event)" />
      </EventPropRow>
      <EventPropRow label="X">
        <VariableIndexField v-if="isUserVar" :model-value="xValue" scope="var" title="选择 X 变量" @update:model-value="updateNumber('X', $event)" />
        <NumberStepperField v-else class="cell-input sm" :model-value="xValue" @update:model-value="updateNumber('X', $event)" />
      </EventPropRow>
      <EventPropRow label="Y">
        <VariableIndexField v-if="isUserVar" :model-value="yValue" scope="var" title="选择 Y 变量" @update:model-value="updateNumber('Y', $event)" />
        <NumberStepperField v-else class="cell-input sm" :model-value="yValue" @update:model-value="updateNumber('Y', $event)" />
      </EventPropRow>
    </template>

    <div v-else class="custom-ui-prop-empty">初始化节点没有属性</div>
  </div>
</template>

<style scoped>
.custom-ui-prop-form {
  min-width: 0;
}

.cell-input {
  height: 28px;
  padding: 4px 8px;
  border: 1px solid var(--theme-bg-secondary);
  background: var(--theme-bg-surface);
  color: var(--theme-text-heading);
  font-size: 13px;
  box-sizing: border-box;
}

.cell-input:focus {
  outline: none;
  border-color: var(--theme-accent);
}

.cell-checkbox {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
}

.color-line {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 6px;
}

.custom-ui-prop-empty {
  padding: 14px 10px;
  color: var(--theme-text-muted);
  font-size: 13px;
  line-height: 1.5;
}
</style>
